import { useMemo } from 'react';
import { BeveledPanel } from './FabProcessTool/BeveledPanel';
import { getBrushedAluminumMaps } from '../../../../utils/equipmentCabinetTextures';
import { getSharedStandardMaterial } from '../../../../utils/threeResourcePool';
import { FAB_COLORS, FAB_LED_INTENSITY } from '../rendering/fabColors';
import { getCircleFrameMaterial, getCircleLoadPortFaceGlassMaterial } from '../Stocker/CircleStocker/materials';

const HOLE = '#3A4248';
const TILES = 4;
const HOLE_COLS = 4;
const HOLE_ROWS = 2;

type Props = {
  width: number;
  depth: number;
  /** World Y of the top of the deck plate. */
  y: number;
  z: number;
  x?: number;
  /** Dark plinth under the aluminum pad. */
  baseHeight?: number;
  /** 0–1 shutter / transfer activity — brightens the cyan rim. */
  active?: number;
  /** Raised aluminum rim around the pad — off for stocker tray docks. */
  showOuterFrame?: boolean;
  /** Cyan glass deck lid + front status strip (stocker load ports). */
  showStockerDeck?: boolean;
  opacity?: number;
};

/**
 * Brushed-aluminum kinematic pad: raised metal frame, 4×4 holed plates,
 * inner cyan seam, and a dark matte base under the ports.
 */
export function LoadPortAlignGrid({
  width,
  depth,
  y,
  z,
  x = 0,
  baseHeight,
  active = 0,
  showOuterFrame = true,
  showStockerDeck = false,
  opacity = 1,
}: Props) {
  const frameW = width;
  const frameD = depth;
  const rim = showOuterFrame ? Math.max(0.03, Math.min(width, depth) * 0.07) : 0;
  const innerW = frameW - rim * 2;
  const innerD = frameD - rim * 2;
  const plateH = Math.max(0.01, Math.min(width, depth) * 0.016);
  const frameH = plateH * 2.2;
  const baseH = Math.max(baseHeight ?? Math.min(width, depth) * 0.22, plateH * 4);
  const gap = Math.min(innerW, innerD) * 0.016;
  const tileW = (innerW - gap * (TILES - 1)) / TILES;
  const tileD = (innerD - gap * (TILES - 1)) / TILES;
  const holeR = Math.min(tileW, tileD) * 0.055;
  const glow = 0.34 + active * 0.38;
  const maps = useMemo(() => getBrushedAluminumMaps(), []);
  const tileR = Math.min(tileW, tileD, plateH) * 0.12;

  const baseMat = useMemo(() => getCircleFrameMaterial(opacity), [opacity]);
  const deckGlass = useMemo(() => getCircleLoadPortFaceGlassMaterial(opacity), [opacity]);
  const tileMat = useMemo(
    () =>
      getSharedStandardMaterial({
        color: FAB_COLORS.bodyBright,
        map: maps.map,
        roughnessMap: maps.roughnessMap,
        metalness: 0.84,
        roughness: 0.26,
        envMapIntensity: 0.88,
        transparent: opacity < 1,
        opacity,
      }),
    [maps.map, maps.roughnessMap, opacity]
  );
  const rimMat = useMemo(
    () =>
      getSharedStandardMaterial({
        color: FAB_COLORS.bodyHighlight,
        map: maps.map,
        roughnessMap: maps.roughnessMap,
        metalness: 0.88,
        roughness: 0.24,
        envMapIntensity: 0.88,
        transparent: opacity < 1,
        opacity,
      }),
    [maps.map, maps.roughnessMap, opacity]
  );
  const cyanMat = useMemo(
    () =>
      getSharedStandardMaterial({
        color: FAB_COLORS.cyan,
        emissive: FAB_COLORS.cyan,
        emissiveIntensity: glow * FAB_LED_INTENSITY.cyanSoft,
        toneMapped: false,
        transparent: opacity < 1,
        opacity,
      }),
    [glow, opacity]
  );
  const holeMat = useMemo(
    () =>
      getSharedStandardMaterial({
        color: HOLE,
        metalness: 0.28,
        roughness: 0.68,
        transparent: opacity < 1,
        opacity,
      }),
    [opacity]
  );

  const tiles = useMemo(() => {
    const nodes: Array<{ key: string; x: number; z: number }> = [];
    for (let ix = 0; ix < TILES; ix++) {
      for (let iz = 0; iz < TILES; iz++) {
        nodes.push({
          key: `tile-${ix}-${iz}`,
          x: -innerW / 2 + tileW / 2 + ix * (tileW + gap),
          z: -innerD / 2 + tileD / 2 + iz * (tileD + gap),
        });
      }
    }
    return nodes;
  }, [gap, innerD, innerW, tileD, tileW]);

  const holes = useMemo(() => {
    const xs = Array.from({ length: HOLE_COLS }, (_, i) => -0.32 + (i / (HOLE_COLS - 1)) * 0.64);
    const zs = Array.from({ length: HOLE_ROWS }, (_, i) => -0.2 + (i / (HOLE_ROWS - 1)) * 0.4);
    return xs.flatMap((ox) => zs.map((oz) => [ox, oz] as const));
  }, []);

  return (
    <group position={[x, y, z]} name="LoadPortAlignGrid">
      <BeveledPanel
        size={[frameW * 0.9, baseH, frameD * 0.88]}
        material={baseMat}
        position={[0, -plateH * 0.5 - baseH * 0.5, 0]}
        radius={Math.min(frameW, frameD) * 0.02}
        smoothness={3}
        castShadow
        receiveShadow
      />

      {tiles.map((tile) => (
        <group key={tile.key} position={[tile.x, 0, tile.z]}>
          <BeveledPanel
            size={[tileW, plateH, tileD]}
            material={tileMat}
            radius={tileR}
            smoothness={3}
            receiveShadow
          />
          {holes.map(([ox, oz], i) => (
            <mesh
              key={`hole-${i}`}
              position={[ox * tileW, plateH * 0.52, oz * tileD]}
              rotation={[-Math.PI / 2, 0, 0]}
              material={holeMat}
            >
              <circleGeometry args={[holeR, 12]} />
            </mesh>
          ))}
        </group>
      ))}

      {showOuterFrame ? (
        <>
          {([-1, 1] as const).map((side) => (
            <BeveledPanel
              key={`frame-x-${side}`}
              size={[rim, frameH, frameD]}
              material={rimMat}
              position={[side * (frameW * 0.5 - rim * 0.5), frameH * 0.15, 0]}
              radius={rim * 0.25}
              smoothness={3}
              castShadow
            />
          ))}
          {([-1, 1] as const).map((side) => (
            <BeveledPanel
              key={`frame-z-${side}`}
              size={[frameW - rim * 2, frameH, rim]}
              material={rimMat}
              position={[0, frameH * 0.15, side * (frameD * 0.5 - rim * 0.5)]}
              radius={rim * 0.25}
              smoothness={3}
              castShadow
            />
          ))}
          {([-1, 1] as const).flatMap((sx) =>
            ([-1, 1] as const).map((sz) => (
              <mesh
                key={`frame-corner-${sx}-${sz}`}
                position={[sx * (frameW * 0.5 - rim * 0.5), frameH * 0.15, sz * (frameD * 0.5 - rim * 0.5)]}
                material={rimMat}
                castShadow
              >
                <cylinderGeometry args={[rim * 0.5, rim * 0.5, frameH, 16]} />
              </mesh>
            ))
          )}
        </>
      ) : null}

      {([-1, 1] as const).map((side) => (
        <BeveledPanel
          key={`glow-x-${side}`}
          size={[plateH * 0.45, plateH * 0.55, innerD]}
          material={cyanMat}
          position={[side * (innerW * 0.5 + gap * 0.15), plateH * 0.35, 0]}
          radius={plateH * 0.12}
          smoothness={2}
        />
      ))}
      {([-1, 1] as const).map((side) => (
        <BeveledPanel
          key={`glow-z-${side}`}
          size={[innerW, plateH * 0.55, plateH * 0.45]}
          material={cyanMat}
          position={[0, plateH * 0.35, side * (innerD * 0.5 + gap * 0.15)]}
          radius={plateH * 0.12}
          smoothness={2}
        />
      ))}

      {showStockerDeck ? (
        <>
          <BeveledPanel
            size={[innerW * 0.92, plateH * 0.35, innerD * 0.88]}
            material={deckGlass}
            position={[0, plateH * 0.85, 0]}
            radius={tileR * 0.8}
            smoothness={3}
          />
          <BeveledPanel
            size={[innerW * 0.78, plateH * 0.35, plateH * 0.35]}
            material={cyanMat}
            position={[0, plateH * 0.55, innerD * 0.5 + plateH * 0.2]}
            radius={plateH * 0.15}
            smoothness={2}
          />
        </>
      ) : null}
    </group>
  );
}
